import React, { useState, useCallback } from 'react';
import { ReportFilters, SearchQuery, FacetsResult } from '../hooks/useReports';
import FilterDrawer from './FilterDrawer';

interface MobileSearchInterfaceProps {
  onSearch: (query: SearchQuery) => void;
  onFilter: (filters: ReportFilters) => void;
  onFacetedSearch: (filters: Omit<ReportFilters, 'startAfter' | 'limit'>) => void;
  facets?: FacetsResult['facets'];
  searchMetadata?: {
    query: string;
    resultsCount: number;
    searchTime: number;
  };
  isLoading?: boolean;
}

const MobileSearchInterface: React.FC<MobileSearchInterfaceProps> = ({
  onSearch,
  onFilter,
  onFacetedSearch: _onFacetedSearch,
  facets,
  searchMetadata,
  isLoading = false
}) => {
  const [searchText, setSearchText] = useState('');
  const [showFilterDrawer, setShowFilterDrawer] = useState(false);
  const [currentFilters, setCurrentFilters] = useState<ReportFilters>({
    sortBy: 'submittedAt',
    sortOrder: 'desc',
    limit: 20
  });

  // Handle search submission
  const handleSearch = useCallback(() => {
    if (searchText.trim()) {
      const searchQuery: SearchQuery = {
        text: searchText.trim(),
        sortBy: 'relevance',
        sortOrder: 'desc',
        limit: 20
      };
      onSearch(searchQuery);
    } else {
      // If no search text, use regular filtering
      onFilter(currentFilters);
    }
  }, [searchText, currentFilters, onSearch, onFilter]);

  // Handle filter application from drawer
  const handleApplyFilters = useCallback((filters: ReportFilters) => {
    setCurrentFilters(filters);
    onFilter(filters);
  }, [onFilter]);

  // Handle clear filters
  const handleClearFilters = useCallback(() => {
    const clearedFilters: ReportFilters = {
      sortBy: 'submittedAt',
      sortOrder: 'desc',
      limit: 20
    };
    setCurrentFilters(clearedFilters);
    setSearchText('');
    onFilter(clearedFilters);
  }, [onFilter]);

  // Handle Enter key in search input
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  }, [handleSearch]);

  // Count active filters
  const activeFilterCount = Object.keys(currentFilters).filter(key => {
    const value = currentFilters[key as keyof ReportFilters];
    if (key === 'sortBy' || key === 'sortOrder' || key === 'limit') return false;
    if (Array.isArray(value)) return value.length > 0;
    return value !== undefined && value !== '';
  }).length;

  return (
    <div className="bg-white border border-urban-200 rounded-xl p-4 mb-4 shadow-sm">
      {/* Search Bar */}
      <div className="flex gap-2 mb-3">
        <div className="flex-1 relative">
          <input
            type="text"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Search treasures..."
            className="input-mobile w-full pr-12"
            disabled={isLoading}
          />
          {searchText && (
            <button
              onClick={() => setSearchText('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-urban-400 hover:text-urban touch-target w-8 h-8 flex items-center justify-center"
              aria-label="Clear search"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        <button
          onClick={handleSearch}
          disabled={isLoading}
          className="btn-mobile-small bg-cardboard text-white hover:bg-cardboard-600 disabled:opacity-50 px-6"
        >
          {isLoading ? (
            <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          )}
        </button>
      </div>

      {/* Filter and Sort Controls */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => setShowFilterDrawer(true)}
          className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium text-urban transition-colors touch-target"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.707A1 1 0 013 7V4z" />
          </svg>
          <span>Filters</span>
          {activeFilterCount > 0 && (
            <span className="bg-cardboard text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
              {activeFilterCount}
            </span>
          )}
        </button>

        <div className="flex items-center gap-2">
          <span className="text-xs text-urban-400">Sort:</span>
          <select
            value={`${currentFilters.sortBy}-${currentFilters.sortOrder}`}
            onChange={(e) => {
              const [sortBy, sortOrder] = e.target.value.split('-');
              const newFilters = {
                ...currentFilters,
                sortBy: sortBy as ReportFilters['sortBy'],
                sortOrder: sortOrder as ReportFilters['sortOrder']
              };
              setCurrentFilters(newFilters);
              onFilter(newFilters);
            }}
            className="text-xs bg-white border border-gray-300 rounded-lg px-2 py-1 focus:ring-2 focus:ring-cardboard focus:border-cardboard"
            disabled={isLoading}
          >
            <option value="submittedAt-desc">Newest</option>
            <option value="submittedAt-asc">Oldest</option>
            <option value="pickupDate-asc">Pickup Date</option>
            <option value="viewCount-desc">Most Viewed</option>
          </select>
        </div>
      </div>

      {/* Search Metadata */}
      {searchMetadata && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <p className="text-xs text-urban-400">
            Found {searchMetadata.resultsCount} results for "{searchMetadata.query}" 
            in {searchMetadata.searchTime}ms
          </p>
        </div>
      )}

      {/* Filter Drawer */}
      <FilterDrawer
        isOpen={showFilterDrawer}
        onClose={() => setShowFilterDrawer(false)}
        onApplyFilters={handleApplyFilters}
        onClearFilters={handleClearFilters}
        currentFilters={currentFilters}
        facets={facets}
      />
    </div>
  );
};

export default MobileSearchInterface;